import React from 'react'
import type { PresentationBlock, PresentationPalette } from 'paintscout'
import { ColorOption, ButtonSelectOption } from '../components'
import VerticalAlignTopIcon from '@material-ui/icons/VerticalAlignTop'
import VerticalAlignCenterIcon from '@material-ui/icons/VerticalAlignCenter'
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt'
import FormatColorFillIcon from '@material-ui/icons/FormatColorFill'

interface GeneralOptionsProps {
  block: PresentationBlock
  palette: PresentationPalette
  useCart: boolean
  handleOptionFieldChange: (field: string, value: any) => void
}

// a plain function (not a hook consumer) so it can also be called from `hasBlockOptions`
// outside of a render, to determine up-front whether a block has anything to configure
function GeneralOptions(props: GeneralOptionsProps) {
  const { block, palette, useCart, handleOptionFieldChange } = props
  const { background = {}, fields = {}, type } = block
  const { color: customBgColor, useTheme } = background
  const { verticalAlign = 'center', sectionHeight = 'sm', contentWidth = 'full' } = fields
  const isEmbeddedQuote = type === 'quote' && !useCart
  const fullWidthImage2Col = type === '2-column' && fields.fullWidthImage

  // custom HTML/CSS blocks manage their own background/format via CSS, and slider-image is
  // fixed at a flush x-sm height with no background — neither has any options (#21821)
  if (isEmbeddedQuote || ['custom', 'slider-image'].includes(type)) return []

  const backgroundColor = customBgColor ? customBgColor : useTheme ? palette?.colors.background : ''

  const presetColors = palette?.title
    ? Object.values(palette.colors)
    : ['#FFFFFF', '#F0F0F0', '#E5E4E2', '#FAF9F6', '#F9F6EE', '#FCF5E5', '#EDEADE', '#FFFFF0']

  const hasBackgroundColor = backgroundColor && !['#fff', '#ffffff'].includes(backgroundColor.toLowerCase())

  const options = [
    {
      Component: (
        <ButtonSelectOption
          label={'Use Background Color'}
          value={background.useTheme}
          buttons={[
            {
              icon: <FormatColorFillIcon />,
              value: true,
              disabled: !!customBgColor
            }
          ]}
          onChange={(_value) => handleOptionFieldChange('background.useTheme', !background.useTheme)}
        />
      ),
      tab: 'background'
    },
    {
      Component: (
        <ColorOption
          label={'Custom Background Color'}
          value={customBgColor}
          useClearOption={true}
          helpTextForClear={'Remove Background Color'}
          defaultColor={''}
          clearColor={''}
          presetColors={presetColors}
          onChange={(value) => {
            handleOptionFieldChange('background.color', value)
          }}
        />
      ),
      tab: 'background'
    },
    ...(!fullWidthImage2Col
      ? [
          {
            Component: (
              <ButtonSelectOption
                label={`Block Height`}
                value={sectionHeight}
                buttons={[
                  { icon: 'XS', value: 'x-sm' },
                  { icon: 'S', value: 'sm' },
                  { icon: 'M', value: 'md' },
                  { icon: 'L', value: 'lg' }
                ]}
                onChange={(value) => {
                  handleOptionFieldChange('fields.sectionHeight', value)
                }}
              />
            ),
            tab: 'format'
          }
        ]
      : []),
    ...(!['spacer'].includes(type) && !fullWidthImage2Col
      ? [
          {
            Component: (
              <ButtonSelectOption
                label={'Content Width'}
                value={contentWidth}
                buttons={[
                  // { icon: 'XS', value: 'x-sm' },
                  // { icon: 'S', value: 'sm' },
                  {
                    icon: 'M',
                    value: 'md',
                    forceSelected: contentWidth === 'full' && hasBackgroundColor && !['banner'].includes(type),
                    disabled: hasBackgroundColor && !['banner'].includes(type)
                  },
                  {
                    icon: 'L',
                    value: 'full',
                    disabled: hasBackgroundColor && !['banner'].includes(type)
                  }
                ]}
                infoText={
                  contentWidth === 'full' && hasBackgroundColor && !['banner'].includes(type)
                    ? `Blocks with background colors must be medium width`
                    : ''
                }
                onChange={(value) => {
                  handleOptionFieldChange('fields.contentWidth', value)
                }}
              />
            ),
            tab: 'format'
          },
          {
            Component: (
              <ButtonSelectOption
                label={'Content Alignment'}
                value={verticalAlign}
                buttons={[
                  { icon: <VerticalAlignTopIcon />, value: 'start' },
                  { icon: <ArrowRightAltIcon style={{ transform: 'rotate(-90deg)' }} />, value: 'top' },
                  { icon: <VerticalAlignCenterIcon />, value: 'center' },
                  { icon: <ArrowRightAltIcon style={{ transform: 'rotate(90deg)' }} />, value: 'bottom' },
                  { icon: <VerticalAlignTopIcon style={{ transform: 'rotate(180deg)' }} />, value: 'end' }
                ]}
                onChange={(value) => {
                  handleOptionFieldChange('fields.verticalAlign', value)
                }}
              />
            ),
            tab: 'format'
          }
        ]
      : [])
  ]

  return options
}

export default GeneralOptions
